import { Plan } from '@wix/ambassador-checkout-server/types';
import _ from 'lodash';
import {
  PricingPlan,
  ReservedLocationIds,
  Service,
  StaffMember,
} from '@wix/bookings-uou-types';
import { CalendarState } from '../../components/BookingCalendar/controller';
import { CalendarStatus } from '../../components/BookingCalendar/ViewModel/widgetViewModel/widgetViewModel';
import { FilterTypes } from '../../components/BookingCalendar/ViewModel/filterViewModel/filterViewModel';
import {
  BookingsQueryParams,
  CalendarSelections,
  WixOOISDKAdapter,
} from '@wix/bookings-adapter-ooi-wix-sdk';
import { EmptyStateType } from '../../components/BookingCalendar/ViewModel/emptyStateViewModel/emptyStateViewModel';
import { ServiceOptionsAndVariants } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { Optional, Preset, SlotsStatus } from '../../types/types';
import { getValidPurchasedPricingPlansForService } from '../pricingPlans/pricingPlans';
import { Balance } from '@wix/ambassador-pricing-plan-benefits-server/types';
import { Booking } from '@wix/bookings-checkout-api';
import { getAvailableServicesByPreset } from './getAvailableServicesByPreset';
import { extractQueryParamValues } from '../queryParams/queryParams';
import { Experiments } from '@wix/yoshi-flow-editor';
import { DO_NOT_CARE_STAFF } from '../../constants/constants';

export function createInitialState({
  servicesInView,
  allCalendarBookableServices,
  wixSdkAdapter,
  staffMembers,
  rescheduleBookingDetails,
  initialErrors,
  allPurchasedPricingPlans,
  isPricingPlanInstalled,
  isUserLoggedIn,
  servicesVariants,
  preset,
  experiments,
  calendarSelections,
}: {
  servicesInView: Service[];
  allCalendarBookableServices?: Service[];
  wixSdkAdapter: WixOOISDKAdapter;
  staffMembers?: StaffMember[];
  rescheduleBookingDetails?: Booking;
  initialErrors: EmptyStateType[];
  allPurchasedPricingPlans?: Balance[];
  isPricingPlanInstalled: boolean;
  isUserLoggedIn: boolean;
  servicesVariants?: ServiceOptionsAndVariants[];
  preset: Preset;
  experiments?: Experiments;
  calendarSelections?: CalendarSelections;
}): CalendarState {
  const locationFilterOptions = initializeLocationFilterOptions({
    wixSdkAdapter,
    calendarSelections,
  });
  const staffFilterOptions = initializeStaffMembersFilterOptions({
    staffMembers,
    wixSdkAdapter,
    preset,
    servicesInView,
    experiments,
    calendarSelections,
  });
  const purchasedPricingPlans = initializePurchasedPricingPlans({
    servicesInView,
    allPurchasedPricingPlans,
    isPricingPlanInstalled,
    isUserLoggedIn,
  });
  const selectedDate = initializeSelectedDate(wixSdkAdapter);

  const serviceVariantsMap = createServicesVariantsMap(servicesVariants);

  return {
    calendarStatus: CalendarStatus.INITIALIZING,
    slotsStatus: SlotsStatus.LOADING,
    servicesInView,
    allCalendarBookableServices,
    selectedDate,
    selectedTimezone: undefined,
    selectedTime: undefined,
    availableSlots: undefined,
    selectableSlotsAtSelectedTime: undefined,
    selectedRange: undefined,
    availableSlotsPerDay: undefined,
    selectedBookingPreferences: [],
    calendarErrors: [],
    rescheduleBookingDetails,
    dialog: undefined,
    filterOptions: {
      [FilterTypes.SERVICE]: [],
      [FilterTypes.LOCATION]: locationFilterOptions,
      [FilterTypes.STAFF_MEMBER]: staffFilterOptions,
    },
    initialErrors,
    purchasedPricingPlans,
    isUserLoggedIn,
    selectedVariantsOptions: [],
    serviceVariantsMap,
  };
}

const createServicesVariantsMap = (
  serviceVariants?: ServiceOptionsAndVariants[],
) => {
  if (!serviceVariants) {
    return {};
  }

  return serviceVariants.reduce<Record<string, ServiceOptionsAndVariants>>(
    (acc, serviceVariant) => {
      acc[serviceVariant.serviceId!] = serviceVariant;
      return acc;
    },
    {},
  );
};

const initializeLocationFilterOptions = ({
  wixSdkAdapter,
  calendarSelections,
}: {
  wixSdkAdapter: WixOOISDKAdapter;
  calendarSelections?: CalendarSelections;
}): string[] => {
  const preSelectedLocations = calendarSelections?.location
    ? [calendarSelections.location]
    : extractQueryParamValues(wixSdkAdapter, BookingsQueryParams.LOCATION);

  return preSelectedLocations.filter(
    (location) => location !== ReservedLocationIds.OTHER_LOCATIONS,
  );
};

const initializeStaffMembersFilterOptions = ({
  servicesInView,
  wixSdkAdapter,
  preset,
  staffMembers,
  experiments,
  calendarSelections,
}: {
  servicesInView: Service[];
  wixSdkAdapter: WixOOISDKAdapter;
  preset: Preset;
  staffMembers?: StaffMember[];
  experiments?: Experiments;
  calendarSelections?: CalendarSelections;
}): string[] => {
  const staffFilterOptions: string[] = [];
  const isMultiServiceAppointmentEnabled = experiments?.enabled(
    'specs.bookings.multiServiceAppointment',
  );
  if (isMultiServiceAppointmentEnabled) {
    if (
      calendarSelections?.services &&
      calendarSelections?.services.length === 1
    ) {
      staffFilterOptions.push(calendarSelections.services[0].resources[0]);
      return staffFilterOptions;
    }
    const resourcesIds = extractQueryParamValues(
      wixSdkAdapter,
      BookingsQueryParams.RESOURCE,
    );
    if (resourcesIds && resourcesIds.includes(DO_NOT_CARE_STAFF)) {
      staffFilterOptions.push(DO_NOT_CARE_STAFF);
    } else {
      staffFilterOptions.push(
        ...initializeStaffMemberFilterOptionsFromQuery({
          servicesInView,
          wixSdkAdapter,
          preset,
          staffMembers,
          experiments,
        }),
      );
    }
    return staffFilterOptions;
  }
  if (staffMembers) {
    const staffMemberId = staffMembers?.[0]?.id;
    if (staffMemberId) {
      const services = experiments?.enabled(
        'specs.bookings.calendarServicesDropdownRefactor',
      )
        ? servicesInView
        : getAvailableServicesByPreset({
            servicesInView,
            preset,
          });
      const isStaffMemberConnectedToService = services.some((service) =>
        _.find(service?.staffMembers, ({ id }) => id === staffMemberId),
      );
      if (isStaffMemberConnectedToService) {
        staffFilterOptions.push(staffMemberId);
      }
    }
  } else {
    staffFilterOptions.push(
      ...initializeStaffMemberFilterOptionsFromQuery({
        servicesInView,
        wixSdkAdapter,
        preset,
        staffMembers,
        experiments,
      }),
    );
  }

  return staffFilterOptions;
};
const initializeStaffMemberFilterOptionsFromQuery = ({
  servicesInView,
  wixSdkAdapter,
  preset,
  experiments,
}: {
  servicesInView: Service[];
  wixSdkAdapter: WixOOISDKAdapter;
  preset: Preset;
  staffMembers?: StaffMember[];
  experiments?: Experiments;
}): string[] => {
  const staffFilterOptions: string[] = [];
  const resourcesIds = extractQueryParamValues(
    wixSdkAdapter,
    BookingsQueryParams.RESOURCE,
  );
  if (resourcesIds.length) {
    const services = experiments?.enabled(
      'specs.bookings.calendarServicesDropdownRefactor',
    )
      ? servicesInView
      : getAvailableServicesByPreset({
          servicesInView,
          preset,
        });
    resourcesIds.forEach((resourceId) => {
      const isStaffMemberConnectedToService = services.some((service) =>
        _.find(service?.staffMembers, ({ id }) => id === resourceId),
      );
      if (isStaffMemberConnectedToService) {
        staffFilterOptions.push(resourceId);
      }
    });
  }
  return staffFilterOptions;
};
const initializePurchasedPricingPlans = ({
  servicesInView,
  allPurchasedPricingPlans,
  isPricingPlanInstalled,
  isUserLoggedIn,
}: {
  servicesInView: Service[];
  allPurchasedPricingPlans?: Balance[];
  isPricingPlanInstalled: boolean;
  isUserLoggedIn: boolean;
}): Plan[] => {
  let purchasedPricingPlans: Plan[] = [];
  let servicePricingPlans: PricingPlan[] = [];

  if (isPricingPlanInstalled) {
    if (isUserLoggedIn) {
      servicePricingPlans =
        (servicesInView.length &&
          servicesInView[0].payment.pricingPlanInfo?.pricingPlans) ||
        [];
      purchasedPricingPlans = getValidPurchasedPricingPlansForService({
        servicePricingPlans,
        allPurchasedPricingPlans: allPurchasedPricingPlans || [],
      });
    }
  }
  return purchasedPricingPlans;
};

const initializeSelectedDate = (
  wixSdkAdapter: WixOOISDKAdapter,
): Optional<string> => {
  return wixSdkAdapter.getUrlQueryParamValue(BookingsQueryParams.DATE);
};
